/** Contact — enquiry form (stored for admin review) + configurable channels. */
import { useState } from "react";
import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { Container, Section, Breadcrumb, PageHeader, AccentItalic, Card, CardBody, Button, Alert } from "../components/ui";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePublicSettings, whatsappHref } from "../lib/usePublicSettings";

const inputCls = "w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve";

function Field({ label, children }) {
  return <label className="block"><span className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">{label}</span>{children}</label>;
}

function Channels({ links }) {
  const wa = whatsappHref(links);
  const rows = [
    links?.contact_email && { Icon: Mail, label: links.contact_email, href: `mailto:${links.contact_email}`, id: "email" },
    links?.contact_phone && { Icon: Phone, label: links.contact_phone, href: `tel:${links.contact_phone.replace(/\s/g, "")}`, id: "phone" },
    wa && { Icon: MessageCircle, label: "Chat on WhatsApp", href: wa, id: "whatsapp" },
    { Icon: MapPin, label: "Haridwar, Uttarakhand, India", id: "address" },
  ].filter(Boolean);
  return (
    <ul className="space-y-4">
      {rows.map(({ Icon, label, href, id }) => (
        <li key={id} className="flex items-center gap-3 text-[14px]" data-testid={`contact-channel-${id}`}>
          <span className="w-10 h-10 rounded-full border border-line-2 bg-surface grid place-items-center text-mauve"><Icon size={16} /></span>
          {href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="hover:text-mauve">{label}</a> : <span className="text-text-dim">{label}</span>}
        </li>
      ))}
    </ul>
  );
}

export function ContactPage() {
  const settings = usePublicSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "general", subject: "", message: "" });
  const [state, setState] = useState({ stage: "idle" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setState({ stage: "loading" });
    try { await api.post("/enquiries", form); setState({ stage: "sent" }); }
    catch (err) { setState({ stage: "error", msg: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  return (
    <Section padded="sm" data-testid="contact-page">
      <Container>
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }, { label: "Contact" }]} />
        <PageHeader eyebrow="We read every message" title={<>Get in <AccentItalic>touch</AccentItalic></>} lede="Questions about courses, sadhana, products or services — write to us and our team will respond." className="mt-6" />
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          <Card variant="elevated"><CardBody className="p-7">
            {state.stage === "sent" ? (
              <div data-testid="contact-sent"><h3 className="font-display text-[22px]">Thank you.</h3><p className="text-text-dim text-[14px] mt-2">Your enquiry has been received. Our team will reply to {form.email}.</p></div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" /></Field>
                  <Field label="Email"><input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" /></Field>
                  <Field label="Phone (optional)"><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" /></Field>
                  <Field label="Topic">
                    <select value={form.topic} onChange={set("topic")} className={inputCls} data-testid="contact-topic">
                      {["general", "courses", "sadhana", "products", "services", "orders", "donation"].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Subject"><input value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject" /></Field>
                <Field label="Message"><textarea required rows={6} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message" /></Field>
                {state.stage === "error" && <Alert variant="error" data-testid="contact-error">{state.msg}</Alert>}
                <Button type="submit" disabled={state.stage === "loading"} data-testid="contact-submit">{state.stage === "loading" ? "Sending…" : "Send enquiry"}</Button>
              </form>
            )}
          </CardBody></Card>
          <Card variant="elevated"><CardBody className="p-7"><Channels links={settings?.about_links} /></CardBody></Card>
        </div>
      </Container>
    </Section>
  );
}
